import * as platform from '@platform';

/**
 * Runtime accent-colour picker, ported from the reference project.
 * Each option only rewrites a handful of HSL custom properties, so every
 * Tailwind `primary`/`accent`/`ring` utility repaints at once.
 * Labels are i18n keys (the PRD forbids hard-coded user-facing copy).
 */
export interface ThemeOption {
  id: string;
  /** i18n key for the swatch label */
  labelKey: string;
  /** HSL triplet used for --primary */
  primary: string;
  /** soft tint used for --accent */
  accent: string;
  accentForeground: string;
  ring: string;
  /** preview swatch shown in the colour fan */
  swatch: string;
}

const PURPLE: ThemeOption = {
  id: 'purple',
  labelKey: 'theme.color.purple',
  primary: '265 52% 50%',
  accent: '268 50% 94%',
  accentForeground: '265 50% 32%',
  ring: '265 52% 58%',
  swatch: 'hsl(265 52% 50%)',
};

export const THEMES: ThemeOption[] = [
  PURPLE,
  { id: 'indigo', labelKey: 'theme.color.indigo', primary: '243 60% 55%', accent: '243 60% 94%', accentForeground: '243 55% 32%', ring: '243 60% 62%', swatch: 'hsl(243 60% 55%)' },
  { id: 'blue', labelKey: 'theme.color.blue', primary: '214 78% 50%', accent: '214 70% 94%', accentForeground: '214 70% 30%', ring: '214 78% 58%', swatch: 'hsl(214 78% 50%)' },
  { id: 'cyan', labelKey: 'theme.color.cyan', primary: '190 75% 42%', accent: '190 70% 93%', accentForeground: '190 70% 26%', ring: '190 75% 50%', swatch: 'hsl(190 75% 42%)' },
  { id: 'teal', labelKey: 'theme.color.teal', primary: '172 62% 38%', accent: '172 55% 92%', accentForeground: '172 60% 24%', ring: '172 62% 46%', swatch: 'hsl(172 62% 38%)' },
  { id: 'green', labelKey: 'theme.color.green', primary: '150 55% 38%', accent: '150 50% 92%', accentForeground: '150 55% 24%', ring: '150 55% 46%', swatch: 'hsl(150 55% 38%)' },
  { id: 'lime', labelKey: 'theme.color.lime', primary: '88 55% 40%', accent: '88 50% 92%', accentForeground: '88 55% 24%', ring: '88 55% 48%', swatch: 'hsl(88 55% 40%)' },
  { id: 'gold', labelKey: 'theme.color.gold', primary: '42 88% 46%', accent: '42 80% 92%', accentForeground: '38 70% 28%', ring: '42 88% 54%', swatch: 'hsl(42 88% 46%)' },
  { id: 'orange', labelKey: 'theme.color.orange', primary: '24 88% 52%', accent: '24 85% 93%', accentForeground: '24 70% 30%', ring: '24 88% 58%', swatch: 'hsl(24 88% 52%)' },
  { id: 'red', labelKey: 'theme.color.red', primary: '0 72% 52%', accent: '0 70% 94%', accentForeground: '0 60% 32%', ring: '0 72% 60%', swatch: 'hsl(0 72% 52%)' },
  { id: 'pink', labelKey: 'theme.color.pink', primary: '330 70% 52%', accent: '330 70% 94%', accentForeground: '330 60% 32%', ring: '330 70% 60%', swatch: 'hsl(330 70% 52%)' },
  { id: 'slate', labelKey: 'theme.color.slate', primary: '220 20% 35%', accent: '220 20% 92%', accentForeground: '220 25% 22%', ring: '220 20% 45%', swatch: 'hsl(220 20% 35%)' },
  { id: 'violet', labelKey: 'theme.color.violet', primary: '280 62% 52%', accent: '280 60% 94%', accentForeground: '280 55% 32%', ring: '280 62% 60%', swatch: 'hsl(280 62% 52%)' },
  { id: 'lavender', labelKey: 'theme.color.lavender', primary: '255 45% 62%', accent: '255 50% 95%', accentForeground: '255 45% 34%', ring: '255 45% 68%', swatch: 'hsl(255 45% 62%)' },
  { id: 'fuchsia', labelKey: 'theme.color.fuchsia', primary: '300 65% 50%', accent: '300 60% 94%', accentForeground: '300 55% 30%', ring: '300 65% 58%', swatch: 'hsl(300 65% 50%)' },
  { id: 'rose', labelKey: 'theme.color.rose', primary: '348 72% 55%', accent: '348 70% 95%', accentForeground: '348 60% 32%', ring: '348 72% 62%', swatch: 'hsl(348 72% 55%)' },
  { id: 'coral', labelKey: 'theme.color.coral', primary: '12 78% 56%', accent: '12 75% 94%', accentForeground: '12 65% 32%', ring: '12 78% 62%', swatch: 'hsl(12 78% 56%)' },
  { id: 'amber', labelKey: 'theme.color.amber', primary: '35 90% 50%', accent: '35 85% 93%', accentForeground: '32 70% 30%', ring: '35 90% 56%', swatch: 'hsl(35 90% 50%)' },
  { id: 'olive', labelKey: 'theme.color.olive', primary: '70 45% 38%', accent: '70 45% 92%', accentForeground: '70 45% 24%', ring: '70 45% 46%', swatch: 'hsl(70 45% 38%)' },
  { id: 'emerald', labelKey: 'theme.color.emerald', primary: '160 60% 36%', accent: '160 55% 92%', accentForeground: '160 55% 22%', ring: '160 60% 44%', swatch: 'hsl(160 60% 36%)' },
  { id: 'sky', labelKey: 'theme.color.sky', primary: '202 82% 48%', accent: '202 78% 94%', accentForeground: '202 70% 28%', ring: '202 82% 56%', swatch: 'hsl(202 82% 48%)' },
  { id: 'navy', labelKey: 'theme.color.navy', primary: '225 55% 38%', accent: '225 50% 93%', accentForeground: '225 50% 24%', ring: '225 55% 46%', swatch: 'hsl(225 55% 38%)' },
  { id: 'steel', labelKey: 'theme.color.steel', primary: '205 25% 42%', accent: '205 25% 92%', accentForeground: '205 30% 26%', ring: '205 25% 50%', swatch: 'hsl(205 25% 42%)' },
  { id: 'mocha', labelKey: 'theme.color.mocha', primary: '20 35% 40%', accent: '20 35% 92%', accentForeground: '20 35% 24%', ring: '20 35% 48%', swatch: 'hsl(20 35% 40%)' },
  { id: 'plum', labelKey: 'theme.color.plum', primary: '310 40% 38%', accent: '310 40% 93%', accentForeground: '310 40% 24%', ring: '310 40% 46%', swatch: 'hsl(310 40% 38%)' },
  { id: 'black', labelKey: 'theme.color.black', primary: '0 0% 18%', accent: '0 0% 92%', accentForeground: '0 0% 15%', ring: '0 0% 30%', swatch: 'hsl(0 0% 18%)' },
];

export const THEME_STORAGE_KEY = 'worki_theme_color';
export const DARK_STORAGE_KEY = 'worki_dark_mode';
export const LIST_GRADIENT_STORAGE_KEY = 'worki_list_gradient';
export const BORDER_INTENSITY_STORAGE_KEY = 'worki_border_intensity';
export const INVERT_COLORS_STORAGE_KEY = 'worki_invert_colors';

/**
 * How strong the accent wash over every list is, out of 100. Both ends of the
 * scale — the curve, the two alphas, the stronger pair a dark ground needs —
 * are stated in design.css; this side only says how far along it the app sits.
 *
 * The default is a wash that is seen rather than looked for. A list with no
 * tint on it at all is what 0 is for, and it is a setting rather than the
 * resting state because the lists reading as one flat sheet is the thing this
 * was asked for to fix.
 */
export const DEFAULT_LIST_GRADIENT = 40;
export const DEFAULT_BORDER_INTENSITY = 50;

export function currentBorderIntensity(): number {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(BORDER_INTENSITY_STORAGE_KEY) ?? 'null');
    return typeof value === 'number' && Number.isFinite(value)
      ? Math.min(100, Math.max(0, Math.round(value)))
      : DEFAULT_BORDER_INTENSITY;
  } catch {
    return DEFAULT_BORDER_INTENSITY;
  }
}

export function currentInvertColors(): boolean {
  try {
    return localStorage.getItem(INVERT_COLORS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** What `clampGradient` is to the wash: a slider's value, made fit to be painted or stored. */
function clampBorderIntensity(value: number): number {
  return Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : DEFAULT_BORDER_INTENSITY;
}

export function setBorderIntensity(value: number): void {
  const intensity = clampBorderIntensity(value);
  try {
    localStorage.setItem(BORDER_INTENSITY_STORAGE_KEY, JSON.stringify(intensity));
  } catch {
    // Keep the preference usable when storage is unavailable.
  }
  applyBorderIntensity(document.documentElement, intensity);
}

function applyBorderIntensity(root: HTMLElement, intensity: number): void {
  const dark = isDarkMode();
  const lightness = dark ? 12 + intensity * 0.24 : 100 - intensity * 0.2;
  for (const token of ['--border', '--input']) {
    root.style.setProperty(token, `${dark ? '266 14%' : '268 24%'} ${lightness}%`);
  }
}

export function setInvertColors(on: boolean): void {
  const preferredDark = currentDarkPreference();
  try {
    localStorage.setItem(INVERT_COLORS_STORAGE_KEY, JSON.stringify(on));
  } catch {
    // Keep the preference usable when storage is unavailable.
  }
  document.documentElement.classList.toggle('dark', preferredDark !== on);
  loadTheme();
}

/** The chosen mode, before the optional foreground/background inversion. */
export function currentDarkPreference(): boolean {
  return isDarkMode() !== currentInvertColors();
}

/**
 * The accent tint for a dark ground. The light tints above are ~94% lightness,
 * which on a dark canvas is a white block; this keeps the hue and saturation the
 * user picked and only moves the lightness, so a chip stays recognisably the
 * accent colour in both modes.
 *
 * These have to be computed rather than looked up because `applyTheme` writes
 * inline custom properties on <html>, and an inline value outranks any `.dark`
 * rule a stylesheet could carry.
 */
function darkTint(primary: string, lightness: string): string {
  const [hue = '265', saturation = '52%'] = primary.split(' ');
  return `${hue} ${saturation} ${lightness}`;
}

/**
 * The rail's own token family, as the purple default states it in design.css.
 * Saturation is written as it appears there, against a primary of 52% — the
 * ratio below rescales it for whichever accent is picked.
 */
const SIDEBAR: Array<[token: string, saturation: number, lightness: number]> = [
  ['background', 40, 22],
  ['foreground', 30, 92],
  ['primary', 80, 78],
  ['primary-foreground', 40, 18],
  ['accent', 35, 30],
  ['accent-foreground', 30, 96],
  ['border', 30, 33],
  ['ring', 80, 78],
];

/** What `.dark` overrides in the stylesheet, which an inline value outranks. */
const SIDEBAR_DARK: Record<string, [saturation: number, lightness: number]> = {
  background: [30, 12],
  accent: [24, 20],
  border: [20, 24],
};

/** The saturation the purple default was drawn against. */
const REFERENCE_SATURATION = 52;

/**
 * The rail is painted from `--sidebar-*`, a family of its own that nothing here
 * used to touch — so picking gold left a purple rail beside gold buttons. The
 * tokens are rebuilt from the chosen accent instead of being looked up: the hue
 * is taken as-is, and each saturation is scaled by how saturated the accent is
 * against the purple these numbers were written for. Purple therefore
 * reproduces design.css exactly, `black` (0% saturation) gives a grey rail
 * rather than a grey-labelled purple one, and the ceiling keeps a 90% accent
 * like amber from turning the rail into a neon block.
 */
function applySidebar(root: HTMLElement, primary: string, dark: boolean): void {
  const [hue = '265', saturation = '52%'] = primary.split(' ');
  const ratio = Math.min(Number.parseFloat(saturation) / REFERENCE_SATURATION, 1.35);

  for (const [token, sat, light] of SIDEBAR) {
    const [darkSat, darkLight] = SIDEBAR_DARK[token] ?? [];
    const s = dark && darkSat !== undefined ? darkSat : sat;
    const l = dark && darkLight !== undefined ? darkLight : light;
    // 80% scaled by 1.35 is 108, which is not a saturation.
    const scaled = Math.min(Math.round(s * ratio), 100);
    root.style.setProperty(`--sidebar-${token}`, `${hue} ${scaled}% ${l}%`);
  }
}

export function isDarkMode(): boolean {
  return document.documentElement.classList.contains('dark');
}

/** A slider that hands back 140, or NaN, has not earned the right to be stored. */
function clampGradient(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_LIST_GRADIENT;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/**
 * The stylesheets multiply their tint by this, so the strength is one number on
 * <html> rather than a colour computed here: the wash is `hsl(var(--primary))`,
 * which means an accent change or the dark switch repaints every list with no
 * second write from this side.
 */
function applyListGradient(root: HTMLElement, value: number): void {
  root.style.setProperty('--list-gradient', String(value / 100));
}

export function applyTheme(id: string): void {
  const t = THEMES.find((x) => x.id === id) ?? PURPLE;
  const root = document.documentElement;
  const dark = isDarkMode();
  root.style.setProperty('--primary', t.primary);
  root.style.setProperty('--accent', dark ? darkTint(t.primary, '24%') : t.accent);
  const foreground = dark ? darkTint(t.primary, '86%') : t.accentForeground;
  root.style.setProperty('--accent-foreground', foreground);
  root.style.setProperty('--secondary-foreground', foreground);
  /*
   * The accent used as *writing* rather than as a surface.
   *
   * `--primary` is picked to be readable with white on top of it, which is the
   * opposite requirement: at 50% lightness the default purple is 6.6:1 on white
   * and 2.6:1 on the dark card, so every eyebrow, link and pill label drawn in
   * the accent goes nearly invisible the moment the lights go out. 72% is the
   * floor that clears 4.5:1 for the least contrasty accent on the list (purple,
   * on the raised card); every other accent sits above it, amber at 10:1.
   *
   * Surfaces keep `--primary` — a button lightened to 72% would need dark text
   * and would stop being the same colour as the brand.
   */
  root.style.setProperty('--primary-text', dark ? darkTint(t.primary, '72%') : t.primary);
  root.style.setProperty('--ring', t.ring);
  applySidebar(root, t.primary, dark);
  // Every path that repaints the document — the first load, an accent change,
  // the dark switch — arrives here, so the wash is written here rather than in
  // a fourth place that any one of them could forget to call.
  applyListGradient(root, currentListGradient());
  applyBorderIntensity(root, currentBorderIntensity());
  void platform.setStatusBarColor(hslTripletToHex(t.primary), dark);
}

function hslTripletToHex(triplet: string): string {
  const match = triplet.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  const h = Number(match?.[1] ?? 265) / 360;
  const s = Number(match?.[2] ?? 52) / 100;
  const l = Number(match?.[3] ?? 50) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** The stored accent, or the default when nothing is stored or it is unreadable. */
export function currentThemeId(): string {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    const id = raw ? (JSON.parse(raw) as unknown) : null;
    return typeof id === 'string' && THEMES.some((x) => x.id === id) ? id : PURPLE.id;
  } catch {
    return PURPLE.id;
  }
}

/** Applies an accent and remembers it. The storage format lives here only. */
export function setTheme(id: string): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(id));
  } catch {
    // A browser with storage blocked still gets the colour for this session.
  }
  applyTheme(id);
}

/** The stored strength, or the default when nothing is stored or it is unreadable. */
export function currentListGradient(): number {
  try {
    const raw = localStorage.getItem(LIST_GRADIENT_STORAGE_KEY);
    const value = raw ? (JSON.parse(raw) as unknown) : null;
    return typeof value === 'number' ? clampGradient(value) : DEFAULT_LIST_GRADIENT;
  } catch {
    return DEFAULT_LIST_GRADIENT;
  }
}

/** Applies a wash strength and remembers it. The storage format lives here only. */
export function setListGradient(value: number): void {
  const strength = clampGradient(value);
  try {
    localStorage.setItem(LIST_GRADIENT_STORAGE_KEY, JSON.stringify(strength));
  } catch {
    // A browser with storage blocked still gets the strength for this session.
  }
  applyListGradient(document.documentElement, strength);
}

/**
 * Dark mode is a class on the root, so both token families — the legacy
 * `--color-*` set and the ported HSL set — swap in one stylesheet rule. The
 * accent is reapplied afterwards because its tints differ by mode.
 */
export function applyDarkMode(on: boolean): void {
  document.documentElement.classList.toggle('dark', on !== currentInvertColors());
  try {
    localStorage.setItem(DARK_STORAGE_KEY, JSON.stringify(on));
  } catch {
    // A browser with storage blocked still gets the mode for this session.
  }
  loadTheme();
}

/** Reads the stored preference and applies it. Returns what was applied. */
export function loadDarkMode(): boolean {
  const stored = readStoredDark();
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const on = stored ?? query.matches;
  document.documentElement.classList.toggle('dark', on !== currentInvertColors());
  if (stored === null) listenForSystemDark(query);
  return on;
}

function readStoredDark(): boolean | null {
  try {
    const raw = localStorage.getItem(DARK_STORAGE_KEY);
    if (raw === null) return null;
    return raw === 'true';
  } catch {
    return null;
  }
}

function listenForSystemDark(query: MediaQueryList): void {
  const onChange = () => {
    if (readStoredDark() !== null) return;
    document.documentElement.classList.toggle('dark', query.matches !== currentInvertColors());
    loadTheme();
  };
  query.addEventListener('change', onChange);
}

/**
 * Marks the document root with the design variant. Called before the first paint
 * so the auth screens and the public pages get the same treatment as the app.
 * There is no second design to switch to any more, so this is unconditional.
 */
export function loadDesignVariant(): void {
  document.documentElement.classList.add('design-v2');
}

export function loadTheme(): void {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    const id = raw ? (JSON.parse(raw) as unknown) : PURPLE.id;
    applyTheme(typeof id === 'string' ? id : PURPLE.id);
  } catch {
    applyTheme(PURPLE.id);
  }
}

/**
 * Paints an accent, a mode and the two sliders on the page without storing any
 * of them, so the profile screen can show a choice before it is kept. Only the
 * setters above write to storage, which is what keeps trying a look and keeping
 * it two separate acts.
 *
 * The order matters. The mode goes first because the accent's tints and the
 * border both read it off the root, and the sliders go last because `applyTheme`
 * repaints them from storage on its way through.
 *
 * A draft inversion is previewed with the other choices; callers that omit it
 * retain the stored preference.
 */
export function previewLook(look: { themeId: string; dark: boolean; gradient: number; borderIntensity: number; inverted?: boolean }): void {
  const root = document.documentElement;
  root.classList.toggle('dark', look.dark !== (look.inverted ?? currentInvertColors()));
  applyTheme(look.themeId);
  applyListGradient(root, clampGradient(look.gradient));
  applyBorderIntensity(root, clampBorderIntensity(look.borderIntensity));
}

/**
 * Puts back what is stored: the accent, the mode (the system's, when none was
 * ever chosen) and both sliders. A preview that was not saved must not outlive
 * the screen that showed it, and one that was saved is already what is stored,
 * so leaving either way comes here.
 *
 * `loadDarkMode` is not reused: it starts listening to the system whenever
 * nothing is stored, and this runs on every visit to the screen.
 */
export function restoreStoredLook(): void {
  const chosen = readStoredDark() ?? window.matchMedia('(prefers-color-scheme: dark)').matches;
  document.documentElement.classList.toggle('dark', chosen !== currentInvertColors());
  loadTheme();
}
